import { v4 as uuid } from 'uuid';
import {
  SELECT_MODELS,
  SET_MODEL_OFFSET,
  SET_VIEWER_INSTANCE,
  ViewerActionType,
  COLOR_OBJETS_MODEL,
  COLOR_BI_TAGS,
  SELECT_TAGS,
  SELECT_TAG_NAMES,
  ISOLATE_TAGS,
  ISOLATE_TAG_NAMES,
  SELECTED_OBJECT_IN_VIEWER,
  OPEN_PANEL,
  OPEN_PANELS,
  CHANGE_POSITION_CAMERA,
  CAMERA_POSITION_CHANGED,
  EXTENSION_ACTIVE_CHANGED,
  SELECT_AGGREGATE_TAGS,
  SELECTED_STATUS_MODES,
  CUTPLANES_CHANGED,
  SELECT_CUTPLANES,
  EDIT_CUTPLANES,
  SET_CUSTOM_ENTITES_COLORED,
  OPEN_BI_REPORT,
  SET_SCREENSHOT_ACTION,
  SET_SCREENSHOT_URL,
  CHANGE_SELECTION_LEVEL,
  SET_MAX_SELECTION_LEVEL,
  CREATE_SNAPSHOT,
  SET_MODEL_PROPERTIES,
  SAVE_VIEWER_SETTINGS,
  APPLY_VIEWER_SETTINGS,
  SET_IS_MOBILE,
  SET_IS_LESSTHANXSMALL,
  SET_PANEL_SCROLL_POSITION,
  UPDATE_OPEN_PANELS,
  CLOSE_PANEL,
  UPDATE_PANEL_POSITION,
  SET_IS_REFRESHING,
  SET_LOADING_ITEMS,
  UPDATE_THUMBNAIL_IMAGE,
  SET_MARKUP_ACTIVE,
  APPLY_PUSHPIN_FROM_PBI,
  SET_LOADED_URN_LIST,
  SET_IS_DIFF_TOOL_LOADED,
  SET_EXTENDED_PROPERTY_PANEL_HANDLER,
  SET_PBI_REPORT_STATE,
  SET_IS_PBI_MODE_ACTIVE,
  SET_IS_PBI_NEW_WINDOW_ACTIVE,
  TRIGGER_PBI_REPORT_REFRESH,
  SET_IS_REFRESH_DOCUMENTS_IN_COMMENT_PANEL,
  SET_COLORED_WP_IDS,
  SET_CUTPLANE_FILTER_MODE
} from '../actionTypes';
import { ViewerState } from '../stateTypes';

const initialState: ViewerState = {
  selectedPlant: null,
  selectedUnits: [],
  selectedStatusModes: [],
  cutplanes: null,
  selectionLevel: 1,
  maxSelectionLevel: 0,
  scrollPosition: {},
  openPanels: [],
  panelPositions: {},
  isRefreshing: false as boolean,
  loadingItems: [],
  thumbnails: new Map<string, { image: string, lastRefreshDate: string }>(),
  isPushpinActiveFromPBI: false,
  cutplaneFilterMode: 'strict' // Default to strict mode (completely inside)
};

export function viewerReducer(
  state = initialState,
  action: ViewerActionType
): ViewerState {
  switch (action.type) {
    case SELECT_MODELS:
      return {...state, 
        selectedPlant: action.payload.selectedPlant,
        selectedUnits: action.payload.selectedUnits,
        selectedTagInViewer: action.payload.selectedTagInViewer
      };
    case SET_MODEL_OFFSET:
      return {...state, 
        globalOffset: action.payload.globalOffset
      };
    case SET_VIEWER_INSTANCE:
      return {...state, 
        viewerInstance: action.payload.viewerInstance
      };
    case SET_MODEL_PROPERTIES:
      return {...state, 
        properties: action.payload.properties
      };
    case COLOR_OBJETS_MODEL:
      return {...state, 
        entitiesColored: action.payload.entitiesColored,
        selectedEntitiesColoredActionId: uuid()
      };
    case SET_COLORED_WP_IDS:
      return {...state, 
        coloredWPIds: action.payload.coloredWPIds
      };
    case COLOR_BI_TAGS:
      return {...state, 
        coloredBITags: action.payload.coloredBITags,
        coloredBITagsActionId: uuid(),
        multiplePKConfig: action.payload.multiplePKConfig,
        searchType: action.payload.searchType || ""
      };
    case SELECT_TAGS:
      return {...state, 
        selectedTags: action.payload.selectedTags,
        selectedTagsActionId: uuid()
      };
    case SELECT_TAG_NAMES:
      return {...state, 
        selectedTagNames: action.payload.selectedTagNames,
        selectedTagNamesActionId: uuid(),
        selectedCheckbox: action.payload.selectedCheckbox,
        multiplePKConfig: action.payload.multiplePKConfig,
        searchType: action.payload.searchType || ""
      };
    case SELECT_AGGREGATE_TAGS:
      return {...state, 
        selectedAggregateTags: action.payload.selectedAggregateTags
      };
    case ISOLATE_TAGS:
      return {...state, 
        isolatedTags: action.payload.isolatedTags,
        isolatedTagsActionId: uuid()
      };
    case ISOLATE_TAG_NAMES:
      return {...state, 
        isolatedTagNames: action.payload.isolatedTagNames,
        isolatedTagNamesActionId: uuid(),
        searchType: action.payload.searchType || "",
        multiplePKConfig: action.payload.multiplePKConfig
      };
    case SELECTED_OBJECT_IN_VIEWER:
      return {...state, 
        selectedTagInViewer: action.payload.selectedTagInViewer
      };
    case OPEN_PANEL: {
      const newPanel = action.payload;
      const existingPanels = state.openPanels || [];
      return {
        ...state,
        openPanels: [...existingPanels, newPanel]
      };
    }
    //snapshot copy url with panel open state is not updating the panel state
    case OPEN_PANELS:
    case UPDATE_OPEN_PANELS: {
      return {
        ...state,
        openPanels: action.payload
      };
    }
    case CLOSE_PANEL:
      if (state.openPanels === undefined) return state;
      return {
        ...state,
        openPanels: state.openPanels?.filter(panel => panel.PanelId !== action.payload.panelId)
      };
    case UPDATE_PANEL_POSITION:
      return {
        ...state,
        panelPositions: {
          ...state.panelPositions,
          [action.payload.panelId]: action.payload.position,
        },
      };
    case CAMERA_POSITION_CHANGED:
      return {...state, 
        cameraPosition: action.payload.cameraPosition
      };
    case EXTENSION_ACTIVE_CHANGED:
      return {...state, 
        extension: action.payload.extension
      };
    case CHANGE_POSITION_CAMERA:
      return {...state, 
        changePositionCamera: action.payload.changePositionCamera,
        selectedCameraActionId: uuid()
      };
    case SELECTED_STATUS_MODES:
      return {...state, 
        selectedStatusModes: action.payload.selectedStatusModes
      };
    case SET_CUSTOM_ENTITES_COLORED:
      return {...state, 
        customEntitiesColored: action.payload.customEntitiesColored
      };
    case CUTPLANES_CHANGED:
      return {...state, 
        cutplanes: action.payload.cutplanes
      };
    case SELECT_CUTPLANES:
      return {...state, 
        selectedVolume: action.payload.selectedVolume,
        selectedVolumeActionId: uuid()
      };
    case EDIT_CUTPLANES:
      return {...state, 
        editedVolume: action.payload.editedVolume,
        editedVolumeActionId: uuid()
      };
    case SET_CUTPLANE_FILTER_MODE:
      return {...state, 
        cutplaneFilterMode: action.payload.cutplaneFilterMode
      };
    case OPEN_BI_REPORT:
      return {...state, 
        openedBIReport: action.payload.openedBIReport
      };
    case SET_SCREENSHOT_ACTION:
      return {...state, 
        screenshotAction: action.payload.screenshotAction,
        screenshotActionId: uuid()
      };
    case SET_SCREENSHOT_URL:
      return {...state, 
        screenshotUrl: action.payload.screenshotUrl
      };
    case CHANGE_SELECTION_LEVEL:
      return {...state, 
        selectionLevel: action.payload.selectionLevel
      };
    case SET_MAX_SELECTION_LEVEL:
      return {...state, 
        maxSelectionLevel: action.payload.maxSelectionLevel
      };
    case CREATE_SNAPSHOT:
      return {...state, 
        createdSnapshotId: action.payload.createdSnapshotId
      };
    case SAVE_VIEWER_SETTINGS:
      return {...state, 
        viewerSettings: action.payload.viewerSettings
      };
    case APPLY_VIEWER_SETTINGS:
      return {...state, 
        applyViewerSettings: action.payload.applyViewerSettings
      };
    case SET_IS_MOBILE:
      return {...state, 
        isMobile: action.payload.isMobile
      };
    case SET_IS_LESSTHANXSMALL:
      return {...state, 
        isLessThanXSmall: action.payload.isLessThanXSmall
      };
    case SET_PANEL_SCROLL_POSITION: {
      const containerId = typeof action.payload.containerId === 'string' ? action.payload.containerId : 'default';
      const currentScrollPosition =
        typeof state.scrollPosition === 'object' && state.scrollPosition !== null
          ? state.scrollPosition
          : {};
      const nextScrollPosition =
        typeof action.payload.scrollPosition === 'number'
          ? action.payload.scrollPosition
          : 0;
      return {...state, 
        scrollPosition: {
          ...currentScrollPosition,
          [containerId]: nextScrollPosition,
        },
      };
    }
    case SET_IS_REFRESHING :
      return{
          ...state,
          isRefreshing: action.payload
      };
    case SET_LOADING_ITEMS :
      return {
        ...state,
        loadingItems: action.payload
      };
    case UPDATE_THUMBNAIL_IMAGE:{
      const updatedThumbnails = new Map(state.thumbnails);
      updatedThumbnails.set(action.payload.imageName, {
        image: action.payload.image,
        lastRefreshDate: String(action.payload.lastRefreshDate)
      });
      return {
        ...state,
        thumbnails: updatedThumbnails
      };
    }
      case SET_MARKUP_ACTIVE:
      return {
        ...state,
        isMarkupActive: action.payload,
      };
      case APPLY_PUSHPIN_FROM_PBI:
        return {
          ...state,
          isPushpinActiveFromPBI : action.payload
        }
        case SET_LOADED_URN_LIST:
           return {
          ...state,
          loadedUrnList : action.payload.loadedUrnList
        }
        case SET_IS_DIFF_TOOL_LOADED:
           return {
          ...state,
          isDiffToolLoaded : action.payload.isDiffToolLoaded
        }
        case SET_EXTENDED_PROPERTY_PANEL_HANDLER:
          return {
            ...state,
            openExtendedPropertyPanelHandler: action.payload,
          };
          case SET_PBI_REPORT_STATE:
            return {
            ...state,
            PbiButtonPressed: action.payload.PbiButtonPressed,
            PbiState: action.payload.PbiState,
          }
          case SET_IS_PBI_MODE_ACTIVE:
            return {
            ...state,
            isPBIModeActive: action.payload.isPBIModeActive,
          }
          case SET_IS_PBI_NEW_WINDOW_ACTIVE:
            return {
            ...state,
            isPBINewWindowActive: action.payload.isPBINewWindowActive,
          }
          case TRIGGER_PBI_REPORT_REFRESH:
            return {
            ...state,
            pbiReportRefreshActionId: action.payload.pbiReportRefreshActionId,
          }
          case SET_IS_REFRESH_DOCUMENTS_IN_COMMENT_PANEL:
            return {
            ...state,
            refreshDocumentsInCommentPanel: action.payload.refreshDocumentsInCommentPanel,
          }
    default:
      return state;
  }
}
